import { useCardImage } from "@/lib/hooks/useCardImage";
import { useState, useEffect } from "react";

interface CardImageProps {
  cardName: string | null;
  alt?: string;
  className?: string;
  showPlaceholder?: boolean;
  onLoad?: () => void;
  onError?: (error: string) => void;
  onClick?: () => void;
  clickable?: boolean;
}

function CardImage({
  cardName,
  alt,
  className = "",
  showPlaceholder = true,
  onLoad,
  onError,
  onClick,
  clickable = false,
}: CardImageProps) {
  const { imagePath, isLoading, error } = useCardImage(cardName, true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  useEffect(() => {
    if (imagePath && !isLoading && onLoad) {
      onLoad();
    }
  }, [imagePath, isLoading, onLoad]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(false);
  };

  // Show placeholder while loading or if no card selected
  if (!cardName || isLoading || !imagePath) {
    if (showPlaceholder) {
      return (
        <div
          className={`card-image-placeholder bg-gradient-to-br from-purple-900 via-blue-900 to-black border border-purple-500 flex flex-col items-center justify-center ${className} rounded-lg`}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-300 border-t-transparent mb-2"></div>
              <div className="loading-indicator text-purple-300 text-xs">
                Loading...
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-purple-400 rotate-45 mb-2"></div>
              <div className="w-6 h-6 border border-purple-400 rounded-full"></div>
              <div className="text-purple-300 text-xs mt-2">Touch to draw</div>
            </div>
          )}
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={`card-image-container relative ${className} ${
        clickable
          ? "cursor-pointer hover:scale-105 transition-transform duration-200"
          : ""
      }`}
      onClick={clickable && cardName ? onClick : undefined}
    >
      <img
        src={imagePath}
        alt={alt || cardName}
        className={`card-image w-full h-full object-contain transition-opacity duration-300 ${
          imageLoaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Loading overlay */}
      {!imageLoaded && (
        <div className="card-loading-overlay absolute inset-0 bg-black flex items-center justify-center">
          <div className="loading-text text-white text-xs">Loading...</div>
        </div>
      )}

      {/* Error fallback */}
      {error && (
        <div className="card-error-overlay absolute inset-0 bg-gray-200 dark:bg-gray-700 border-2 border-gray-400 dark:border-gray-600 flex items-center justify-center">
          <div className="error-text text-gray-600 dark:text-gray-300 text-xs text-center px-2">
            Image unavailable
          </div>
        </div>
      )}
    </div>
  );
}

export default CardImage;
